import React, { useState } from 'react';
import { Col, Image } from 'react-bootstrap';
import { GiAirplaneDeparture } from 'react-icons/gi';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { Totals } from '../contract/Totals';
import { calcFee, formatDate, getTotals } from '../../../../../../../../shared/functions/Functions';
import { ContractHistoryAdditionalItemsTable } from './ContractHistoryAdditionalItemsTable';
import { ContractHistoryOfferPlatformTable } from './ContractHistoryOfferPlatformTable';
import { ContractHistoryOfferHeader } from './ContractHistoryOfferHeader';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { TotalTypes } from '../../../../../../../../enum/TotalTypes';
import { IContract } from '../../../../../../../../model/shared/IContract';
import { InfluencerFeeTotals } from '../../../../../../../../shared/components/offer/InfluencerFeeTotals';
import { getTruncatedData } from '../../../../../../../../utils/helpers/getTruncatedData';
import TooltipOverlay from '../../../../../../../../shared/components/tooltip/Tooltip';
import FilePlaceholder from '../../../../../../../../shared/components/placeholder/FilePlaceholder';
import { Fancybox } from '../../../../../../../../shared/components/Fancybox';
import { Fancybox as NativeFancybox } from '@fancyapps/ui';
import { downloadFile } from '../../../../../../../../shared/functions/Functions';
import ItemValueSection from '../../../../../../../../shared/components/offer/ItemValueSection';

interface INegotiationHistoryDetailProps {
    contract?: IContract;
    isCharity: boolean;
}

const ContractHistoryDetail = ({ contract, isCharity }: INegotiationHistoryDetailProps) => {
    const [expended, setExpended] = useState(false);
    const { t } = useTranslation();
    const { isTransparentPricing, campaign } = useSelector((state: IAllStates) => state.contractBrand);
    const feePercentage = Math.round((1 - Number(campaign?.feePercentage ?? 0.0)) * 100);
    const subTotalSum = getTotals(contract?.additionalContractItems, contract?.totalPlatforms);
    const grandTotalForDisplay = isTransparentPricing ? contract?.totalWithFee : subTotalSum;
    const legalDocument = contract?.legalDocuments?.[0];
    const { truncatedName: truncatedFileName, isTruncated: isFileNameTruncated } = getTruncatedData(
        legalDocument?.originalName ?? '',
        30
    );
    const { truncatedName: truncatedUploaderName, isTruncated: isUploaderNameTruncated } = getTruncatedData(
        legalDocument?.uploadedBy?.displayName ?? '',
        30
    );

    const influencerFeePercent = contract?.influencerFeePercentage;
    const hasInfluencerFee = contract?.hasInfluencerFee;
    const isIvConnectedClub = contract?.isIvConnectedClub;
    const influenceFeeSum = hasInfluencerFee ? calcFee(subTotalSum ?? 0, influencerFeePercent) : 0;

    const grandTotalMinusInfluencerFee = Math.round((subTotalSum ?? 0) - influenceFeeSum);

    return (
        <Accordion className="mt-1" expanded={expended}>
            <AccordionSummary className="p-0 border-0" onClick={() => setExpended(!expended)}>
                <div className="d-flex default-radius bg-light py-2 px-4">
                    <Col lg={6} className="d-flex align-items-center px-0">
                        {contract?.counterContractBy ? (
                            <CustomImageHandler
                                altTag={'User Profile'}
                                classes="rounded-img-sm"
                                thumbnailPath={
                                    contract?.counterContractBy.isSupervisor
                                        ? ''
                                        : contract?.counterContractBy?.profilePhoto?.thumbnail
                                }
                                photoPath={
                                    contract?.counterContractBy.isSupervisor
                                        ? ''
                                        : contract?.counterContractBy?.profilePhoto?.thumbnail ?? ''
                                }
                                name={
                                    contract?.counterContractBy?.displayName?.trim() ||
                                    `${contract?.counterContractBy?.firstName ?? ''} ${
                                        contract?.counterContractBy?.lastName ?? ''
                                    }`.trim()
                                }
                            />
                        ) : (
                            <CustomImageHandler
                                altTag={'User Profile'}
                                classes="rounded-img-sm"
                                thumbnailPath={contract?.user?.profilePhoto?.thumbnail}
                                photoPath={contract?.user?.profilePhoto?.thumbnail}
                                name={
                                    contract?.counterContractBy?.displayName?.trim() ||
                                    `${contract?.counterContractBy?.firstName ?? ''} ${
                                        contract?.counterContractBy?.lastName ?? ''
                                    }`.trim()
                                }
                            />
                        )}
                        <div>
                            {contract?.counterContractBy ? (
                                <h6 className="ml-3 mb-1">
                                    {contract?.counterContractBy.isSupervisor
                                        ? 'Customer Success by i.v.'
                                        : !!contract?.counterContractBy?.displayName
                                        ? contract?.counterContractBy?.displayName
                                        : contract?.counterContractBy?.firstName +
                                          ' ' +
                                          contract?.counterContractBy?.lastName}
                                </h6>
                            ) : (
                                <h6 className="ml-3 mb-1">
                                    {!!contract?.user.displayName
                                        ? contract?.user.displayName
                                        : contract?.user?.firstName + ' ' + contract?.user?.lastName}
                                </h6>
                            )}
                            <h6 className="ml-3 mb-0">
                                {formatDate(contract?.createdAt ?? '')} - {contract?.createdAt.substring(11, 16)}
                            </h6>
                        </div>
                    </Col>
                    <Col lg={6} className="d-flex align-items-center justify-content-between py-3 px-0 p-lg-0 ">
                        <div>
                            <div className="d-flex align-items-center">
                                <h5 className="mb-0 text-primary euro-symbol">
                                    {hasInfluencerFee ? grandTotalMinusInfluencerFee : grandTotalForDisplay}
                                </h5>
                            </div>
                            {!!contract?.additionalContractItems?.length && (
                                <span className="text-muted font-10">
                                    <GiAirplaneDeparture className="text-primary mr-1 font-14" />
                                    {t('brand.campaign.book.contract.additional_costs_included')}
                                </span>
                            )}
                        </div>
                        {expended ? (
                            <div className="text-uppercase text-muted d-flex align-items-center">
                                <p className="mb-0">{t('general.button.hide_details')}</p>
                                <Image className="ml-2" alt="image-up" src="/icons/img_arrow_up.svg" />
                            </div>
                        ) : (
                            <div className="text-uppercase text-muted d-flex align-items-center">
                                <p className="mb-0">{t('general.button.see_details')}</p>
                                <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                            </div>
                        )}
                    </Col>
                </div>
            </AccordionSummary>
            <AccordionDetails className="p-0 ">
                <ContractHistoryOfferPlatformTable totalPlatforms={contract?.totalPlatforms} />
                {contract?.additionalContractItems?.length !== 0 && (
                    <>
                        <ContractHistoryAdditionalItemsTable additionalItems={contract?.additionalContractItems} />
                        <Totals
                            title={TotalTypes.ADDITIONAL_ITEMS}
                            price={getTotals(contract?.additionalContractItems)}
                            displayPlus={true}
                        />
                    </>
                )}
                {isTransparentPricing && !isCharity && (
                    <div className="w-100 mt-3">
                        <Totals title={TotalTypes.SUBTOTAL} price={subTotalSum} feePercentage={100 - feePercentage} />
                    </div>
                )}
                {isTransparentPricing && (
                    <div className="w-100 mt-3">
                        <Totals
                            title={TotalTypes.FEE}
                            price={contract?.feeAmount ?? 0}
                            feePercentage={feePercentage}
                            displayPlus={true}
                        />
                    </div>
                )}
                {(!isCharity || (isCharity && grandTotalMinusInfluencerFee > 0)) && (
                    <InfluencerFeeTotals
                        subTotalAndAddons={subTotalSum ?? 0}
                        serviceFeeSum={Math.round(influenceFeeSum)}
                        containerClassName="pt-3"
                        grandTotalSum={grandTotalMinusInfluencerFee}
                        feePercentage={influencerFeePercent}
                        hasFee={hasInfluencerFee}
                        isIvConnectedClub={isIvConnectedClub}
                    />
                )}
                {!hasInfluencerFee && (
                    <div className="mt-3">
                        <Totals
                            title={TotalTypes.GRAND}
                            price={grandTotalForDisplay}
                            feePercentage={isTransparentPricing ? 100 : undefined}
                        />
                    </div>
                )}
                {isCharity && !!campaign?.charityValueDesc && (
                    <ItemValueSection description={campaign?.charityValueDesc} />
                )}
                <ContractHistoryOfferHeader
                    personalOffer={contract?.personalOffer}
                    createdAt={contract?.createdAt ?? ''}
                    userId={contract?.user?.id}
                    counterOfferById={contract?.counterContractBy?.id}
                />
                {legalDocument && (
                    <div className="mt-3">
                        <div
                            className="d-flex align-items-start p-2 border rounded position-relative"
                            style={{ maxWidth: 300 }}
                        >
                            <Fancybox>
                                <FilePlaceholder
                                    file={legalDocument}
                                    hideText={true}
                                    classIcon="font-22"
                                    styleContent={{ width: 40, height: 40, flexShrink: 0 }}
                                    nameFancybox="legal-document"
                                    handleDownload={() =>
                                        downloadFile(legalDocument?.path ?? '', legalDocument?.originalName)
                                    }
                                    right={-10}
                                />
                            </Fancybox>
                            <div className="ms-2">
                                <TooltipOverlay
                                    content={isFileNameTruncated ? legalDocument?.originalName : ''}
                                    dFlex={false}
                                >
                                    <p
                                        className="mb-0 fw-semibold cursor-pointer"
                                        onClick={() =>
                                            NativeFancybox.show([
                                                {
                                                    src: legalDocument?.path,
                                                    type: 'iframe',
                                                },
                                            ])
                                        }
                                    >
                                        {truncatedFileName || '-'}
                                    </p>
                                </TooltipOverlay>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_by')}:{' '}
                                    <TooltipOverlay
                                        content={isUploaderNameTruncated ? legalDocument?.uploadedBy?.displayName : ''}
                                        dFlex={false}
                                    >
                                        <span>{truncatedUploaderName || '-'}</span>
                                    </TooltipOverlay>
                                </p>
                                <p className="mb-0 text-muted fs-12">
                                    {t('influencer.offer_management.modal.uploaded_at')}:{' '}
                                    {formatDate(legalDocument?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default ContractHistoryDetail;
